import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useQuery } from "@apollo/client";
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  TextField,
  InputAdornment,
  IconButton
} from "@mui/material";
import { useHistory } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import GQLQueryStatusIndicator from "../../../components/GQLQueryStatusIndicator";
import { containerStyles } from "../utils/styles";
import { LOAD_VIDEOS_QUERY } from "../../../globals/graphql/moovsAcademy.graphql";
import { useAnalytics } from "globals/hooks";
import { formatVideoDuration } from "./utils/formatDuration";

function MoovsAcademyPage() {
  // hooks
  const [searchTerm, setSearchTerm] = useState("");
  const history = useHistory();
  const { track } = useAnalytics();

  // queries
  const { data: videosData, error: videosError, loading: videosLoading } = useQuery(
    LOAD_VIDEOS_QUERY,
    {
      variables: {
        tags: ["live"],
      },
    }
  );

  // Filter videos based on search term (case-insensitive name matching)
  const filteredVideos = useMemo(() => {
    if (!videosData?.loadVideos) {
      return [];
    }
    if (!searchTerm) {
      return videosData.loadVideos; // Return all if search is empty
    }
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return videosData.loadVideos.filter((video: any) =>
      video.name?.toLowerCase().includes(lowerCaseSearchTerm)
    );
  }, [videosData, searchTerm]);

  if (videosLoading) {
    return (
      <GQLQueryStatusIndicator
        name="Moovs Academy"
        loading={true}
      />
    );
  }

  if (videosError || !videosData) {
    return (
      <GQLQueryStatusIndicator
        name="Moovs Academy"
        error={videosError || new Error("No video data found")}
      />
    );
  }

  const handleVideoClick = (video: any) => {
    // Manually track video selection via segment until we implement wistia analytics
    track("moovsAcademy_videoSelected", {
      videoHashedId: video.hashedId,
      videoName: video.name,
    })
    history.push(`/settings/academy/video/${video.hashedId}`);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  return (
    <>
      <Helmet >
        <title>Moovs Academy | Moovs</title>
      </Helmet>
      <Box sx={containerStyles} >
        {/* Welcome Section */}
        <Box mb={6} mt={6}>
          <Typography variant="h2" gutterBottom>
            Welcome to Moovs Academy
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
           Your one-stop shop for learning everything about Moovs.
          </Typography>
        </Box>

        {/* Search Bar */}
        <Box mb={6}>
          <TextField
            fullWidth
            placeholder="Search for videos by name..."
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: 'white',
                borderRadius: 2,
              }
            }}
          />
        </Box>

        {/* Videos Grid */}
        <Grid container spacing={3} mb={6}>
          {filteredVideos.length > 0 ? (
             filteredVideos.map((video: any) => (
              <Grid item xs={12} sm={6} key={video.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 6,
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}
                  onClick={() => handleVideoClick(video)}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="220"
                      image={video.thumbnail?.url || 'https://via.placeholder.com/400x220'}
                      alt={video.name}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                        width: 64,
                        height: 64,
                      }}
                    >
                      <PlayArrowIcon fontSize="large" />
                    </IconButton>
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h3" gutterBottom>
                      {video.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ minHeight: '3em' }}>
                      {video.description || "No description available"}
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                      <Typography variant="body2" color="text.secondary">
                         {formatVideoDuration(video.duration)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
             ))
          ) : (
            <Grid item xs={12}>
              <Typography>No videos found matching your search.</Typography>
            </Grid>
          )}
        </Grid>

      </Box>
    </>
  );
}

export default MoovsAcademyPage;
